import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlannerContext = createContext();

export function PlannerProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !!user;
  const [planner, setPlanner] = useState({ items: [], total: 0, currency: 'USD' });
  const [loading, setLoading] = useState(false);

  const fetchPlanner = useCallback(async () => {
    // Gated: /api/pjr/planner requires auth. On public pages we keep an
    // empty planner so the navbar badge stays at 0 without paying for a
    // guaranteed 401 round-trip.
    if (!isAuthed) {
      setPlanner({ items: [], total: 0, currency: 'USD' });
      return null;
    }
    try {
      const r = await axios.get(`${API_URL}/api/pjr/planner`, { withCredentials: true });
      setPlanner(r.data);
      return r.data;
    } catch (e) {
      console.error('Error fetching planner:', e);
      return null;
    }
  }, [isAuthed]);

  useEffect(() => {
    if (authLoading) return;
    fetchPlanner();
  }, [authLoading, fetchPlanner]);

  const addToPlanner = async (payload) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/planner/add`, payload, { withCredentials: true });
      setPlanner(r.data.planner);
      return { success: true, item_id: r.data.added_item_id };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo guardar en el Planner' };
    } finally { setLoading(false); }
  };

  const removeFromPlanner = async (itemId) => {
    setLoading(true);
    try {
      const r = await axios.delete(`${API_URL}/api/pjr/planner/remove/${itemId}`, { withCredentials: true });
      setPlanner(r.data.planner);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo quitar' };
    } finally { setLoading(false); }
  };

  const clearPlanner = async () => {
    setLoading(true);
    try {
      const r = await axios.delete(`${API_URL}/api/pjr/planner/clear`, { withCredentials: true });
      setPlanner(r.data.planner);
      return { success: true };
    } catch (e) {
      return { success: false };
    } finally { setLoading(false); }
  };

  const moveAllToCart = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/planner/move-to-cart`, {}, { withCredentials: true });
      setPlanner(r.data.planner);
      return { success: true, ...r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo mover al carrito' };
    } finally { setLoading(false); }
  };

  const sharePlanner = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pjr/planner/share`, {}, { withCredentials: true });
      // Refresh local planner so the share state surfaces immediately.
      await fetchPlanner();
      return { success: true, token: r.data.token };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'No se pudo generar el link' };
    } finally { setLoading(false); }
  };

  const unsharePlanner = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/pjr/planner/unshare`, {}, { withCredentials: true });
      await fetchPlanner();
      return { success: true };
    } catch (e) {
      return { success: false };
    } finally { setLoading(false); }
  };

  const itemCount = planner.items?.length || 0;

  return (
    <PlannerContext.Provider value={{
      planner, loading, fetchPlanner, addToPlanner, removeFromPlanner, clearPlanner, moveAllToCart, sharePlanner, unsharePlanner, itemCount,
    }}>
      {children}
    </PlannerContext.Provider>
  );
}

export const usePlanner = () => {
  const ctx = useContext(PlannerContext);
  if (!ctx) throw new Error('usePlanner must be used within a PlannerProvider');
  return ctx;
};
